/**
 * The Rules room's team rail (`docs/plans/argus.md` decision 35, sketch
 * `docs/design/sketches/argus-rules-teams.html` round 2).
 *
 * Left of the list: each of my teams as a card - its badge, its name, my
 * role in it - with All rules and the team's folders under it; then the
 * teams whose Public rules I can read; then what is mine (Followed, Made by
 * me, My drafts). A red number is open alerts, so a quiet place reads quiet.
 *
 * The rail chooses a *place*; the room filters its list by it. Folders are
 * made, renamed and deleted here. A rule dropped on a folder is the room's
 * to move - the rail only says where it landed.
 *
 * Drop targets are always drawn: Chrome aborts a drag when a drop zone first
 * appears at dragstart, so a drag only changes classes on what is already here.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { myUserId } from '../../state/store.ts';
import type { ArgusRule, ArgusTeam } from '../../api/types.ts';
import { showToast } from '../../ui/toast.ts';
import { createFolder, deleteFolder, renameFolder, rules, teams } from './argus-rules-store.ts';

/** `team:<id>`, `folder:<team>:<folder>`, `public:<team>`, `followed`, `mine` or `drafts`. */
export type RailPlace = string;

const FOLDED_KEY = 'argus.rail.folded';
const CLOSED_KEY = 'argus.rail.closedTeams';

const ICON = {
  all: html`<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 4h10M3 8h10M3 12h7" /></svg>`,
  folder: html`<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 4.5V12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H8L6.5 3.5H3A1 1 0 0 0 2 4.5z" /></svg>`,
  bell: html`<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 11V7a4 4 0 0 1 8 0v4l1 1.5H3zM6.5 14a1.6 1.6 0 0 0 3 0" /></svg>`,
  user: html`<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5.5" r="2.6" /><path d="M3 13.5c.6-2.4 2.6-3.6 5-3.6s4.4 1.2 5 3.6" /></svg>`,
  draft: html`<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13h2.5L12.5 6 10 3.5 3 10.5z" /></svg>`,
  globe: html`<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.6" /><path d="M2.4 8h11.2M8 2.4c1.6 1.7 2.3 3.6 2.3 5.6S9.6 11.9 8 13.6C6.4 11.9 5.7 10 5.7 8S6.4 4.1 8 2.4z" /></svg>`,
  chev: html`<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 6.5 8 10l3.5-3.5" /></svg>`,
  plus: html`<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9" /></svg>`,
  side: html`<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="10" rx="1" /><path d="M6 3v10" /></svg>`,
  fold: html`<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M9.5 4.5 6 8l3.5 3.5" /></svg>`,
};

/** A team's two-letter badge: "Energy Management" -> "EM", "Technical" -> "TE". */
export function teamInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return (words[0] ?? '?').slice(0, 2).toUpperCase();
}

/** One of eight team hues, the same for a team everywhere. Never the Argus hue. */
export function teamHue(id: string): number {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % 8;
}

/** Rules with something open - what the red number counts. */
const alerting = (list: readonly ArgusRule[]): number =>
  list.filter((r) => r.status === 'deployed' && (r.watch?.needs_look ?? 0) > 0).length;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A browser that keeps nothing still folds, for this visit.
  }
}

@customElement('argus-team-rail')
export class ArgusTeamRail extends LightElement {
  private store = new StoreController(this, [rules, teams, myUserId]);

  @property({ attribute: false }) place: RailPlace = '';
  /** May this person make and change folders (`argus:author`)? */
  @property({ type: Boolean }) canFile = false;
  /** The rule being dragged from the list, so other teams' folders can dim. */
  @property({ attribute: false }) dragging: ArgusRule | null = null;

  @state() private folded = readJson<boolean>(FOLDED_KEY, false);
  @state() private closed = new Set<string>(readJson<string[]>(CLOSED_KEY, []));
  /** The folder being renamed in place, or `new:<team>` for one being named. */
  @state() private renaming: string | null = null;
  @state() private menu: { folderId: string; teamId: string; x: number; y: number } | null = null;
  @state() private dropOn: string | null = null;

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.menu) this.menu = null;
    const target = e.target as HTMLElement | null;
    if (e.key !== '[' || e.ctrlKey || e.metaKey || e.altKey) return;
    if (target && (target.closest('input, textarea, select, [contenteditable]') || target.isContentEditable)) return;
    // Only while the list is what is on screen: the editor has its own keys.
    if (!this.isConnected || !this.offsetParent) return;
    this.toggleFold();
  };

  private onOutside = (e: Event): void => {
    if (this.menu && !(e.target as Element | null)?.closest('.argus-rail-menu')) this.menu = null;
  };

  /** Sizes the rail to the room's scroll area, so Fold sits on the window's bottom edge. */
  private fit = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const pane = entry.target as HTMLElement;
    const css = getComputedStyle(pane);
    const top = parseFloat(css.paddingTop) || 0;
    this.style.setProperty('--argus-rail-h', `${Math.floor(pane.clientHeight - top)}px`);
    this.style.setProperty('--argus-rail-top', css.paddingTop);
    this.style.setProperty('--argus-rail-under', css.paddingBottom);
  });

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('pointerdown', this.onOutside, true);
    const pane = this.closest('.argus');
    if (pane) this.fit.observe(pane);
  }

  override disconnectedCallback(): void {
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onOutside, true);
    this.fit.disconnect();
    super.disconnectedCallback();
  }

  private toggleFold(): void {
    this.folded = !this.folded;
    writeJson(FOLDED_KEY, this.folded);
  }

  private choose(place: RailPlace): void {
    this.dispatchEvent(new CustomEvent('argus-place', { detail: { place }, bubbles: true, composed: true }));
  }

  private toggleTeam(team: ArgusTeam): void {
    if (this.folded) {
      // Folded, a badge is a way in: open the rail on that team.
      this.toggleFold();
      this.choose(`team:${team.id}`);
      return;
    }
    const next = new Set(this.closed);
    if (next.has(team.id)) next.delete(team.id);
    else next.add(team.id);
    this.closed = next;
    writeJson(CLOSED_KEY, [...next]);
  }

  private async finishRename(input: HTMLInputElement, save: boolean): Promise<void> {
    const key = this.renaming;
    if (!key) return;
    this.renaming = null;
    const name = input.value.trim();
    if (!save || !name) return;
    if (key.startsWith('new:')) {
      const teamId = key.slice(4);
      const failed = await createFolder(teamId, name);
      if (failed) showToast('error', failed);
      else {
        const made = teams.value.teams.find((t) => t.id === teamId)?.folders.find((f) => f.name === name);
        if (made) this.choose(`folder:${teamId}:${made.id}`);
      }
      return;
    }
    const folder = this.folder(key);
    if (!folder || folder.name === name) return;
    const failed = await renameFolder(key, name);
    if (failed) showToast('error', failed);
  }

  private folder(id: string): { id: string; name: string; group_id: string } | undefined {
    for (const t of teams.value.teams) {
      const f = t.folders.find((x) => x.id === id);
      if (f) return f;
    }
    return undefined;
  }

  private async removeFolder(folderId: string, teamId: string): Promise<void> {
    const folder = this.folder(folderId);
    const failed = await deleteFolder(folderId);
    if (failed) {
      showToast('error', failed);
      return;
    }
    const team = teams.value.teams.find((t) => t.id === teamId);
    showToast('success', `${folder?.name ?? 'The folder'} is deleted. Its rules are at the top of ${team?.name ?? 'the team'}.`);
    if (this.place === `folder:${teamId}:${folderId}`) this.choose(`team:${teamId}`);
  }

  /* ── drag a rule onto a folder ─────────────────────────── */

  /** Where a dragged rule may land: its own team's All rules, or one of its folders. */
  private accepts(teamId: string): boolean {
    return !!this.dragging && this.dragging.group_id === teamId;
  }

  private dropProps(teamId: string, folderId: string | null): Record<string, (e: DragEvent) => void> {
    const key = `${teamId}:${folderId ?? ''}`;
    return {
      dragover: (e: DragEvent) => {
        if (!this.accepts(teamId)) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        if (this.dropOn !== key) this.dropOn = key;
      },
      dragleave: (e: DragEvent) => {
        const to = e.relatedTarget as Node | null;
        if (to && (e.currentTarget as Element).contains(to)) return;
        if (this.dropOn === key) this.dropOn = null;
      },
      drop: (e: DragEvent) => {
        if (!this.accepts(teamId)) return;
        e.preventDefault();
        this.dropOn = null;
        const rule = this.dragging;
        if (!rule || (rule.folder_id ?? null) === folderId) return;
        this.dispatchEvent(
          new CustomEvent('argus-file-rule', { detail: { rule, folderId }, bubbles: true, composed: true }),
        );
      },
    };
  }

  /* ── drawing ───────────────────────────────────────────── */

  private place_(
    key: RailPlace,
    icon: TemplateResult,
    label: string,
    list: readonly ArgusRule[],
    opts: { followed?: boolean; drop?: [string, string | null]; folderId?: string; teamId?: string } = {},
  ): TemplateResult {
    const open = alerting(list);
    const current = this.place === key;
    const drop = opts.drop ? this.dropProps(opts.drop[0], opts.drop[1]) : null;
    const dropKey = opts.drop ? `${opts.drop[0]}:${opts.drop[1] ?? ''}` : null;
    const dim = !!this.dragging && !!opts.drop && !this.accepts(opts.drop[0]);
    const renamingThis = opts.folderId && this.renaming === opts.folderId;
    const inner = html`
      <span class="argus-rail-ico">${icon}</span>
      ${renamingThis
        ? html`<input
            class="argus-rail-rename"
            aria-label="Folder name"
            maxlength="80"
            .value=${label}
            @click=${(e: Event) => e.stopPropagation()}
            @keydown=${(e: KeyboardEvent) => {
              e.stopPropagation();
              if (e.key === 'Enter') void this.finishRename(e.target as HTMLInputElement, true);
              if (e.key === 'Escape') void this.finishRename(e.target as HTMLInputElement, false);
            }}
            @blur=${(e: Event) => void this.finishRename(e.target as HTMLInputElement, true)}
          />`
        : html`<span class="argus-rail-lb">${label}</span>`}
      ${open
        ? html`<span class="argus-rail-al ${opts.followed ? 'is-followed' : ''}" title=${`${open} with something open`}>${open}</span>`
        : nothing}
      <span class="argus-rail-n">${list.length}</span>
    `;
    const cls = `argus-rail-pl ${dropKey && this.dropOn === dropKey ? 'is-drop' : ''} ${dim ? 'is-nodrop' : ''}`;
    if (renamingThis) return html`<div class=${cls} aria-current=${current ? 'true' : 'false'}>${inner}</div>`;
    return html`<button
      type="button"
      class=${cls}
      aria-current=${current ? 'true' : 'false'}
      title=${label}
      @click=${() => this.choose(key)}
      @dblclick=${() => {
        if (opts.folderId && this.canFile) this.renaming = opts.folderId;
      }}
      @contextmenu=${(e: MouseEvent) => {
        if (!opts.folderId || !opts.teamId || !this.canFile) return;
        e.preventDefault();
        this.menu = { folderId: opts.folderId, teamId: opts.teamId, x: e.clientX, y: e.clientY };
      }}
      @dragover=${drop?.dragover}
      @dragleave=${drop?.dragleave}
      @drop=${drop?.drop}
    >
      ${inner}
    </button>`;
  }

  private renderTeam(team: ArgusTeam, list: readonly ArgusRule[]): TemplateResult {
    const mine = list.filter((r) => r.group_id === team.id);
    const role = teams.value.sees_all && !team.in_team
      ? team.is_fallback ? 'Rules of deleted teams' : 'Admin view'
      : team.is_leader ? 'Leader' : teams.value.sees_all ? 'Member · admin' : 'Member';
    const open = !this.closed.has(team.id);
    return html`<div class="argus-rail-team" data-hue=${teamHue(team.id)} data-open=${open ? 'true' : 'false'}>
      <button
        type="button"
        class="argus-rail-team-h"
        aria-expanded=${open ? 'true' : 'false'}
        title=${team.name}
        @click=${() => this.toggleTeam(team)}
      >
        <span class="argus-rail-badge">${teamInitials(team.name)}</span>
        <span class="argus-rail-tname">${team.name}</span>
        <span class="argus-rail-role">${role} · ${team.people} ${team.people === 1 ? 'person' : 'people'}</span>
        <span class="argus-rail-chev">${ICON.chev}</span>
      </button>
      <div class="argus-rail-tw"><div class="argus-rail-tree">
        ${this.place_(`team:${team.id}`, ICON.all, 'All rules', mine, { drop: [team.id, null] })}
        ${team.folders.map((f) =>
          this.place_(`folder:${team.id}:${f.id}`, ICON.folder, f.name, mine.filter((r) => r.folder_id === f.id), {
            drop: [team.id, f.id],
            folderId: f.id,
            teamId: team.id,
          }),
        )}
        ${this.renaming === `new:${team.id}`
          ? html`<div class="argus-rail-pl">
              <span class="argus-rail-ico">${ICON.folder}</span>
              <input
                class="argus-rail-rename"
                aria-label="New folder name"
                maxlength="80"
                placeholder="Folder name"
                @keydown=${(e: KeyboardEvent) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') void this.finishRename(e.target as HTMLInputElement, true);
                  if (e.key === 'Escape') void this.finishRename(e.target as HTMLInputElement, false);
                }}
                @blur=${(e: Event) => void this.finishRename(e.target as HTMLInputElement, true)}
              />
            </div>`
          : this.canFile && (team.in_team || teams.value.sees_all)
            ? html`<button type="button" class="argus-rail-add" @click=${() => (this.renaming = `new:${team.id}`)}>
                ${ICON.plus}<span>New folder</span>
              </button>`
            : nothing}
      </div></div>
    </div>`;
  }

  private renderMenu(): TemplateResult | typeof nothing {
    const at = this.menu;
    if (!at) return nothing;
    const team = teams.value.teams.find((t) => t.id === at.teamId);
    const x = Math.min(at.x, window.innerWidth - 230);
    const y = Math.min(at.y, window.innerHeight - 130);
    return html`<div class="cat-menu argus-rail-menu" role="menu" style=${`left:${x}px; top:${y}px`}>
      <button class="cat-menu-item" role="menuitem" type="button" @click=${() => {
        this.renaming = at.folderId;
        this.menu = null;
      }}>
        <span class="cat-menu-icon" aria-hidden="true">✎</span> Rename
      </button>
      <button class="cat-menu-item" role="menuitem" type="button" @click=${() => {
        this.menu = null;
        void this.removeFolder(at.folderId, at.teamId);
      }}>
        <span class="cat-menu-icon" aria-hidden="true">×</span> Delete folder <small>its rules stay</small>
      </button>
      <p class="argus-rail-menu-hint">Everyone in ${team?.name ?? 'the team'} sees this folder.</p>
    </div>`;
  }

  override updated(): void {
    const input = this.querySelector<HTMLInputElement>('.argus-rail-rename');
    if (input && document.activeElement !== input) {
      input.focus();
      input.select();
    }
  }

  override render(): TemplateResult {
    const list = rules.value;
    const rail = teams.value;
    const myTeamIds = new Set(rail.teams.map((t) => t.id));
    // Other teams whose Public rules I can read, by name.
    const others = new Map<string, string>();
    for (const r of list) if (!myTeamIds.has(r.group_id) && !r.in_team) others.set(r.group_id, r.group_name);
    const me = myUserId.value;
    const made = list.filter((r) => r.owner_id === me);
    const heading = rail.sees_all ? 'All teams' : rail.teams.length > 1 ? 'My teams' : 'My team';
    return html`<nav class="argus-rail ${this.folded ? 'is-folded' : ''}" aria-label="Teams and folders">
      <div class="argus-rail-scroll">
        <div class="argus-rail-grp">
          <div class="argus-rail-gh">${heading}</div>
          ${rail.teams.length
            ? rail.teams.map((t) => this.renderTeam(t, list))
            : html`<p class="argus-rail-none">You are in no team yet. An administrator adds you to one.</p>`}
        </div>
        ${others.size
          ? html`<div class="argus-rail-grp">
              <div class="argus-rail-gh"><span>Public from other teams</span>${ICON.globe}</div>
              ${[...others.entries()]
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => html`<div class="argus-rail-pub" data-hue=${teamHue(id)}>
                  ${this.place_(`public:${id}`, html`<span class="argus-rail-dot"></span>`, name, list.filter((r) => r.group_id === id))}
                </div>`)}
            </div>`
          : nothing}
        <div class="argus-rail-grp">
          <div class="argus-rail-gh">For me</div>
          ${this.place_('followed', ICON.bell, 'Followed', list.filter((r) => r.followed), { followed: true })}
          ${this.place_('mine', ICON.user, 'Made by me', made)}
          ${this.place_('drafts', ICON.draft, 'My drafts', made.filter((r) => r.status === 'draft'))}
        </div>
      </div>
      <div class="argus-rail-foot">
        <button
          type="button"
          title=${this.folded ? 'Open the rail ( [ )' : 'Fold the rail ( [ )'}
          aria-expanded=${this.folded ? 'false' : 'true'}
          @click=${() => this.toggleFold()}
        >
          ${ICON.side}<span>${this.folded ? 'Open' : 'Fold'}</span>
          <i class="argus-rail-fold">${ICON.fold}<kbd>[</kbd></i>
        </button>
      </div>
      ${this.renderMenu()}
    </nav>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-team-rail': ArgusTeamRail;
  }
}
